import { useBlocker, useLocation } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'

export interface UseDrawerCloseGuardOpts {
  /** Whether the form has unsaved changes. */
  isDirty: boolean
  /**
   * Called for clean close paths (X / Escape / Cancel on a clean form,
   * or any unblocked navigation that closes the drawer). Typically
   * `modal.reject(); modal.remove()` (or `modal.rejectRemove()`).
   */
  onClose: () => void
  /**
   * Called when the user clicks "Verwerfen" in the confirm UI. iCare's
   * drawers historically use `modal.hide()` here so the modal stays in
   * the registry. Defaults to `onClose` when omitted.
   */
  onDiscard?: () => void
}

export interface UseDrawerCloseGuardReturn {
  /** Render the `ConfirmClose` UI when this is true. */
  showConfirm: boolean
  /** Wire to `<Drawer onClose>` and the Cancel button. */
  handleClose: () => void
  /** Wire to the `ConfirmClose` "Verwerfen" action. */
  handleDiscard: () => void
  /** Wire to the `ConfirmClose` "Weiter bearbeiten" action. */
  handleCancelClose: () => void
  /**
   * Call before any explicit close path that triggers a navigation
   * (e.g. save → redirect). Suppresses the blocker for this drawer's
   * lifetime so the redirect doesn't trip the confirm.
   */
  beginClose: () => void
}

/**
 * Guards a drawer against accidental close on:
 *  - browser/mobile back & forward
 *  - in-app router navigation (`<Link>`, `router.navigate`)
 *  - tab close / reload (native `beforeunload` confirm while dirty)
 *  - explicit close (X / Escape / Cancel button) via `handleClose`
 *
 * On dirty: shows the host's `ConfirmClose` UI. On clean: closes immediately.
 */
export function useDrawerCloseGuard({
  isDirty,
  onClose,
  onDiscard,
}: UseDrawerCloseGuardOpts): UseDrawerCloseGuardReturn {
  const [showConfirm, setShowConfirm] = useState(false)
  const isClosingRef = useRef(false)

  // Stash callbacks in refs so consumers can pass fresh arrows each render
  // without invalidating effect/callback identities below.
  const onCloseRef = useRef(onClose)
  const onDiscardRef = useRef(onDiscard)
  useEffect(() => {
    onCloseRef.current = onClose
    onDiscardRef.current = onDiscard
  })

  const shouldBlockFn = useCallback(() => isDirty && !isClosingRef.current, [isDirty])

  const blocker = useBlocker({
    shouldBlockFn,
    withResolver: true,
    enableBeforeUnload: () => isDirty,
  })

  useEffect(() => {
    if (blocker.status === 'blocked') {
      setShowConfirm(true)
    } else {
      setShowConfirm(false)
    }
  }, [blocker.status])

  const location = useLocation()
  const initialPathRef = useRef(`${location.pathname}${location.searchStr}${location.hash}`)

  useEffect(() => {
    const current = `${location.pathname}${location.searchStr}${location.hash}`
    if (current !== initialPathRef.current && !isClosingRef.current) {
      isClosingRef.current = true
      onCloseRef.current()
    }
  }, [location.pathname, location.searchStr, location.hash])

  const handleClose = useCallback(() => {
    if (isDirty) {
      setShowConfirm(true)
    } else {
      isClosingRef.current = true
      onCloseRef.current()
    }
  }, [isDirty])

  const handleDiscard = useCallback(() => {
    isClosingRef.current = true
    if (blocker.status === 'blocked') {
      blocker.proceed()
    }
    setShowConfirm(false)
    ;(onDiscardRef.current ?? onCloseRef.current)()
  }, [blocker])

  const handleCancelClose = useCallback(() => {
    if (blocker.status === 'blocked') {
      blocker.reset()
    }
    setShowConfirm(false)
  }, [blocker])

  const beginClose = useCallback(() => {
    isClosingRef.current = true
  }, [])

  return { showConfirm, handleClose, handleDiscard, handleCancelClose, beginClose }
}
